import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, PauseCircle, PlayCircle, Play, TestTube2, Loader2, ExternalLink, LifeBuoy } from "lucide-react";
import { formatDateShort } from "@/lib/date";

const SOURCE_TYPES = [
  { value: "rss", label: "RSS" },
  { value: "api", label: "API" },
  { value: "sitemap", label: "Sitemap" },
  { value: "scraping", label: "Scraping (HTML)" },
];

const CITIES = [
  { value: "ushuaia", label: "Ushuaia" },
  { value: "rio-grande", label: "Río Grande" },
  { value: "tolhuin", label: "Tolhuin" },
  { value: "provincial", label: "Provincial" },
  { value: "nacional", label: "Nacional" },
];

const EMPTY = {
  name: "",
  main_url: "",
  feed_url: "",
  source_type: "rss",
  status: "paused",
  city: "",
  frequency_minutes: 60,
  priority: 50,
  selector_title: "",
  selector_content: "",
  selector_image: "",
  selector_date: "",
  notes: "",
  category_ids: [],
  allowed_image_hosts: [],
  category_mappings: [],
  publication_mode: "process_pending",
  render_js: false,
  render_timeout_ms: 25000,
  render_extra_wait_ms: 1500,
};

export default function AutomationSourcesTab({ sources, onChanged }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [mappingTestText, setMappingTestText] = useState("");
  const [mappingTestResult, setMappingTestResult] = useState(null);
  const [mappingTesting, setMappingTesting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.categories().then((c) => { if (!cancelled) setCategories(c || []); }).catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, []);

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setModalOpen(true);
  };

  const openEdit = (src) => {
    setEditingId(src.id);
    // Normalize category_mappings: backend may return legacy dict or rich list.
    // Every row gets a stable _uid so React can track rows across insert/delete/reorder.
    const genUid = () =>
      (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `m-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    let mappings = src.category_mappings;
    if (mappings && !Array.isArray(mappings) && typeof mappings === "object") {
      mappings = Object.entries(mappings).map(([keyword, category_id], i) => ({
        _uid: genUid(), keyword, category_id, active: true, priority: i,
      }));
    } else if (Array.isArray(mappings)) {
      mappings = mappings.map((r) => ({ _uid: r._uid || genUid(), ...r }));
    } else {
      mappings = [];
    }
    setForm({
      ...EMPTY,
      ...src,
      city: src.city || "",
      selector_title: src.selector_title || "",
      selector_content: src.selector_content || "",
      selector_image: src.selector_image || "",
      selector_date: src.selector_date || "",
      notes: src.notes || "",
      allowed_image_hosts: Array.isArray(src.allowed_image_hosts) ? src.allowed_image_hosts : [],
      category_mappings: mappings,
    });
    setMappingTestText("");
    setMappingTestResult(null);
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Falta el nombre.");
    if (!form.main_url.trim()) return toast.error("Falta la URL principal.");
    setSaving(true);
    try {
      // Strip client-only fields (_uid) from category_mappings before persisting.
      const cleanMappings = (form.category_mappings || []).map(({ _uid, ...rest }) => rest);
      const payload = {
        ...form,
        category_mappings: cleanMappings,
        frequency_minutes: Number(form.frequency_minutes) || 60,
        priority: Number(form.priority) || 50,
      };
      if (editingId) {
        await api.adminAutoUpdateSource(editingId, payload);
        toast.success("Fuente actualizada");
      } else {
        await api.adminAutoCreateSource(payload);
        toast.success("Fuente creada");
      }
      setModalOpen(false);
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar la fuente");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (src) => {
    try {
      await api.adminAutoUpdateSource(src.id, {
        status: src.status === "active" ? "paused" : "active",
      });
      onChanged?.();
    } catch (e) {
      toast.error("No se pudo cambiar el estado");
    }
  };

  const remove = async (src) => {
    if (!window.confirm(`¿Eliminar la fuente "${src.name}"?`)) return;
    try {
      await api.adminAutoDeleteSource(src.id);
      toast.success("Fuente eliminada");
      onChanged?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // 409 → linked items exist, offer cascade delete
      if (status === 409 && typeof detail === "object" && detail?.requires_force) {
        const msg = detail.message || `Hay ${detail.linked_count || "varias"} noticias asociadas.`;
        if (window.confirm(`${msg}\n\n¿Eliminar la fuente Y las noticias asociadas?`)) {
          try {
            await api.adminAutoDeleteSource(src.id, true);
            toast.success(`Fuente eliminada junto con ${detail.linked_count || 0} noticia(s)`);
            onChanged?.();
          } catch (e2) {
            const d2 = e2?.response?.data?.detail;
            toast.error(typeof d2 === "string" ? d2 : (d2?.message || "No se pudo eliminar"));
          }
        }
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "No se pudo eliminar"));
    }
  };

  const testSource = async (src) => {
    setTestingId(src.id);
    setTestResult(null);
    setTestDialogOpen(true);
    try {
      const r = await api.adminAutoTestSource(src.id);
      setTestResult({ source: src, ...r });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setTestResult({ source: src, reachable: false, detected: 0, errors: [typeof detail === "string" ? detail : "Error al probar la fuente"] });
    } finally {
      setTestingId(null);
    }
  };

  const runSource = async (src) => {
    if (runningId) return;
    setRunningId(src.id);
    try {
      await api.adminAutoRunSource(src.id);
      toast.success(`Buscando noticias en "${src.name}"…`);
      setTimeout(() => onChanged?.(), 3000);
    } catch (e) {
      toast.error("No se pudo iniciar la ejecución");
    } finally {
      setRunningId(null);
    }
  };

  const recoverSource = async (src) => {
    if (!window.confirm(
      `¿Recuperar la ejecución de "${src.name}"?\n\nMarcará como "colgada" cualquier ejecución que quedó abierta y permitirá iniciar una nueva.`
    )) return;
    try {
      const res = await api.adminAutoRecoverSource(src.id);
      if (res.recovered > 0) {
        toast.success(`Se recuperaron ${res.recovered} ejecución(es) colgada(s). Ya podés lanzar una nueva.`);
      } else {
        toast.info("No había ninguna ejecución colgada en esta fuente.");
      }
      onChanged?.();
    } catch (e) {
      toast.error("No se pudo recuperar la ejecución");
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-lg font-bold text-blueblack">Fuentes</h2>
          <p className="text-xs text-textgray">Medios y feeds que consumirá el motor de importación en el futuro.</p>
        </div>
        <Button onClick={openNew} data-testid={ATID.automationSourceNew}>
          <Plus className="w-4 h-4 mr-2" /> Nueva fuente
        </Button>
      </div>

      <div className="bg-white border border-softblue overflow-x-auto">
        <table data-testid={ATID.automationSourcesTable} className="w-full text-sm">
          <thead className="bg-lightgray/60 text-left text-[11px] uppercase tracking-widest text-textgray">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Ciudad</th>
              <th className="px-4 py-3">Frecuencia</th>
              <th className="px-4 py-3">Prioridad</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Última corrida</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sources.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-textgray">
                  Todavía no hay fuentes cargadas. Creá la primera para preparar el motor de importación.
                </td>
              </tr>
            )}
            {sources.map((s) => (
              <tr key={s.id} data-testid={ATID.automationSourceRow(s.id)} className="border-t border-softblue/60 hover:bg-lightgray/30">
                <td className="px-4 py-3">
                  <div className="font-semibold text-nightblue">{s.name}</div>
                  <a href={s.main_url} target="_blank" rel="noreferrer" className="text-xs text-turquoise hover:underline break-all">{s.main_url}</a>
                </td>
                <td className="px-4 py-3 uppercase text-[11px] tracking-widest text-textgray">{s.source_type}</td>
                <td className="px-4 py-3">{s.city || "—"}</td>
                <td className="px-4 py-3">{s.frequency_minutes} min</td>
                <td className="px-4 py-3 font-mono">{s.priority}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={s.status === "active"
                    ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                    : "border-textgray/40 text-textgray bg-lightgray/60"}>
                    {s.status === "active" ? "activa" : "pausada"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs text-textgray">{s.last_run_at ? formatDateShort(s.last_run_at) : "nunca"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => testSource(s)} disabled={testingId === s.id} data-testid={ATID.automationSourceTest(s.id)} title="Probar fuente">
                      {testingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => runSource(s)} disabled={runningId === s.id} data-testid={ATID.automationSourceRun(s.id)} title="Ejecutar ahora" className="text-turquoise hover:text-turquoise">
                      {runningId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => recoverSource(s)} data-testid={`automation-source-recover-${s.id}`} title="Recuperar ejecución colgada" className="text-amber-600 hover:text-amber-700">
                      <LifeBuoy className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(s)} title={s.status === "active" ? "Pausar" : "Activar"}>
                      {s.status === "active" ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={ATID.automationSourceEdit(s.id)} title="Editar">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={ATID.automationSourceDelete(s.id)} title="Eliminar" className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid={ATID.automationSourceForm} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar fuente" : "Nueva fuente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray">Nombre del medio *</Label>
                <Input
                  data-testid={ATID.automationSourceFieldName}
                  value={form.name}
                  onChange={(e) => setField({ name: e.target.value })}
                  placeholder="Ej. El Diario del Fin del Mundo"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray">Tipo de fuente *</Label>
                <Select value={form.source_type} onValueChange={(v) => setField({ source_type: v })}>
                  <SelectTrigger data-testid={ATID.automationSourceFieldType}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray">URL principal *</Label>
              <Input
                data-testid={ATID.automationSourceFieldMainUrl}
                value={form.main_url}
                onChange={(e) => setField({ main_url: e.target.value })}
                placeholder="https://…"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray">URL de noticias / RSS</Label>
              <Input
                data-testid={ATID.automationSourceFieldFeedUrl}
                value={form.feed_url || ""}
                onChange={(e) => setField({ feed_url: e.target.value })}
                placeholder="https://…/feed"
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setField({ status: v })}>
                  <SelectTrigger data-testid={ATID.automationSourceFieldStatus}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paused">Pausada</SelectItem>
                    <SelectItem value="active">Activa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray">Ciudad</Label>
                <Select value={form.city || "none"} onValueChange={(v) => setField({ city: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid={ATID.automationSourceFieldCity}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin especificar</SelectItem>
                    {CITIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray">Frecuencia (min)</Label>
                <Input
                  type="number"
                  data-testid={ATID.automationSourceFieldFrequency}
                  value={form.frequency_minutes}
                  min={5}
                  max={10080}
                  onChange={(e) => setField({ frequency_minutes: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-textgray">Prioridad (0–100)</Label>
                <Input
                  type="number"
                  data-testid={ATID.automationSourceFieldPriority}
                  value={form.priority}
                  min={0}
                  max={100}
                  onChange={(e) => setField({ priority: e.target.value })}
                />
              </div>
            </div>

            <div className="pt-3 border-t border-softblue">
              <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-2">Selectores (para scraping HTML)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-textgray">Título</Label>
                  <Input
                    data-testid={ATID.automationSourceFieldSelectorTitle}
                    value={form.selector_title || ""}
                    onChange={(e) => setField({ selector_title: e.target.value })}
                    placeholder="Ej. h1.entry-title"
                    className="font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-textgray">Contenido</Label>
                  <Input
                    data-testid={ATID.automationSourceFieldSelectorContent}
                    value={form.selector_content || ""}
                    onChange={(e) => setField({ selector_content: e.target.value })}
                    placeholder="Ej. div.entry-content"
                    className="font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-textgray">Imagen</Label>
                  <Input
                    data-testid={ATID.automationSourceFieldSelectorImage}
                    value={form.selector_image || ""}
                    onChange={(e) => setField({ selector_image: e.target.value })}
                    placeholder="Ej. meta[property=og:image]"
                    className="font-mono text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs text-textgray">Fecha</Label>
                  <Input
                    data-testid={ATID.automationSourceFieldSelectorDate}
                    value={form.selector_date || ""}
                    onChange={(e) => setField({ selector_date: e.target.value })}
                    placeholder="Ej. time[datetime]"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
              <p className="text-[11px] text-textgray mt-2 italic">
                Estos selectores se guardan ahora, pero recién se van a usar cuando el motor de scraping se active.
              </p>
            </div>

            {/* Modo de publicación editorial */}
            <div className="pt-3 border-t border-softblue">
              <Label className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-2 block">
                Modo de publicación
              </Label>
              <Select
                value={form.publication_mode || "process_pending"}
                onValueChange={(v) => setField({ publication_mode: v })}
              >
                <SelectTrigger data-testid="admin-automation-source-publication-mode" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="detect_only">1. Solo detectar (no procesar con IA)</SelectItem>
                  <SelectItem value="process_pending">2. Procesar con IA y dejar pendiente (recomendado)</SelectItem>
                  <SelectItem value="auto_publish">3. Procesar y publicar automáticamente</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-textgray mt-1 italic">
                La publicación automática sólo se ejecuta si pasan todas las guardas editoriales
                (imagen autorizada, sin alertas, categoría definida, sin contenido sensible).
              </p>
            </div>

            {/* Rendering JS — para sitios SPA (React/Angular/Vue) que no exponen HTML plano */}
            <div className="pt-3 border-t border-softblue">
              <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-2">
                Renderizado JavaScript (para sitios tipo SPA)
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer mb-2">
                <input
                  type="checkbox"
                  data-testid={ATID.automationSourceFieldRenderJs}
                  checked={!!form.render_js}
                  onChange={(e) => setField({ render_js: e.target.checked })}
                />
                <span>Activar renderizado con navegador headless (Playwright)</span>
              </label>
              {form.render_js && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-textgray">Timeout (ms)</Label>
                    <Input
                      data-testid={ATID.automationSourceFieldRenderTimeout}
                      type="number"
                      min={5000}
                      max={60000}
                      step={1000}
                      value={form.render_timeout_ms ?? 25000}
                      onChange={(e) => setField({ render_timeout_ms: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-textgray">Espera extra (ms)</Label>
                    <Input
                      data-testid={ATID.automationSourceFieldRenderExtraWait}
                      type="number"
                      min={0}
                      max={15000}
                      step={500}
                      value={form.render_extra_wait_ms ?? 1500}
                      onChange={(e) => setField({ render_extra_wait_ms: Number(e.target.value) })}
                    />
                  </div>
                </div>
              )}
              <p className="text-[11px] text-textgray mt-2 italic">
                Activá esta opción cuando el sitio no exponga las noticias en el HTML plano (por ejemplo Info3 Noticias).
                Consume más tiempo por corrida.
              </p>
            </div>

            {/* Hosts permitidos para imagen — whitelist estricta por fuente */}
            <div className="pt-3 border-t border-softblue">
              <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-2">
                Hosts autorizados para imágenes (whitelist estricta)
              </div>
              <Textarea
                data-testid={ATID.automationSourceFieldAllowedHosts}
                rows={2}
                value={(form.allowed_image_hosts || []).join(", ")}
                onChange={(e) => setField({
                  allowed_image_hosts: e.target.value.split(/[,\n\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean),
                })}
                placeholder="Ej: cdn.infobae.com, images.infobae.com"
                className="font-mono text-xs"
              />
              <p className="text-[11px] text-textgray mt-1 italic">
                Solo los hosts exactos listados acá se aceptan como imágenes de esta fuente. No se derivan automáticamente
                subdominios del dominio principal.
              </p>
            </div>

            {/* Category mappings — reglas de categoría por fuente */}
            <div className="pt-3 border-t border-softblue" data-testid={ATID.automationSourceMappingsSection}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold">
                  Reglas de categoría (se aplican antes de enviar a revisión)
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const uid = (typeof crypto !== "undefined" && crypto.randomUUID)
                      ? crypto.randomUUID()
                      : `m-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
                    setField({
                      category_mappings: [
                        ...(form.category_mappings || []),
                        { _uid: uid, keyword: "", category_id: "", active: true, priority: (form.category_mappings || []).length },
                      ],
                    });
                  }}
                  data-testid={ATID.automationSourceMappingAdd}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" /> Nueva regla
                </Button>
              </div>
              {(form.category_mappings || []).length === 0 && (
                <p className="text-xs text-textgray italic">Sin reglas. Sin regla configurada, cada noticia queda pendiente de revisión sin categoría hasta que un editor la elija manualmente.</p>
              )}
              <div className="space-y-1.5">
                {(form.category_mappings || []).map((rule, i) => (
                  <div
                    key={rule._uid || `mapping-${rule.keyword}-${rule.category_id}-${i}`}
                    data-testid={ATID.automationSourceMappingRow(i)}
                    className="grid grid-cols-[1fr_1fr_60px_60px_28px] gap-2 items-center bg-lightgray/40 px-2 py-1.5 border border-softblue"
                  >
                    <Input
                      data-testid={ATID.automationSourceMappingKeyword(i)}
                      value={rule.keyword || ""}
                      onChange={(e) => {
                        const next = [...form.category_mappings];
                        next[i] = { ...rule, keyword: e.target.value };
                        setField({ category_mappings: next });
                      }}
                      placeholder="Palabra clave (ej: Policía)"
                      className="text-xs h-8"
                    />
                    <Select
                      value={rule.category_id || ""}
                      onValueChange={(v) => {
                        const next = [...form.category_mappings];
                        next[i] = { ...rule, category_id: v };
                        setField({ category_mappings: next });
                      }}
                    >
                      <SelectTrigger data-testid={ATID.automationSourceMappingCategory(i)} className="h-8 text-xs">
                        <SelectValue placeholder="— Categoría —" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-textgray justify-center cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={ATID.automationSourceMappingActive(i)}
                        checked={!!rule.active}
                        onChange={(e) => {
                          const next = [...form.category_mappings];
                          next[i] = { ...rule, active: e.target.checked };
                          setField({ category_mappings: next });
                        }}
                      />
                      Activa
                    </label>
                    <Input
                      type="number"
                      data-testid={ATID.automationSourceMappingPriority(i)}
                      value={rule.priority ?? i}
                      min={0}
                      max={999}
                      onChange={(e) => {
                        const next = [...form.category_mappings];
                        next[i] = { ...rule, priority: Number(e.target.value) };
                        setField({ category_mappings: next });
                      }}
                      className="text-xs h-8 text-center"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        const next = form.category_mappings.filter((_, idx) => idx !== i);
                        setField({ category_mappings: next });
                      }}
                      data-testid={ATID.automationSourceMappingDelete(i)}
                      className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              {/* Test-mapping widget — siempre visible cuando la fuente ya fue guardada al menos una vez */}
              {editingId && (
                <div className="mt-3 flex items-center gap-2">
                  <Input
                    data-testid={ATID.automationSourceMappingTestInput}
                    value={mappingTestText}
                    onChange={(e) => setMappingTestText(e.target.value)}
                    placeholder="Probar con un texto de categoría"
                    className="text-xs h-8"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={mappingTesting || !mappingTestText.trim()}
                    data-testid={ATID.automationSourceMappingTestBtn}
                    onClick={async () => {
                      setMappingTesting(true);
                      setMappingTestResult(null);
                      try {
                        // Save first (strip _uid) so the server has the latest rules
                        const clean = (form.category_mappings || []).map(({ _uid, ...rest }) => rest);
                        await api.adminAutoUpdateSource(editingId, { category_mappings: clean });
                        const r = await api.adminAutoTestSourceMapping(editingId, { text: mappingTestText });
                        setMappingTestResult(r);
                      } catch (e) {
                        const d = e?.response?.data?.detail;
                        toast.error(typeof d === "string" ? d : "No se pudo probar");
                      } finally {
                        setMappingTesting(false);
                      }
                    }}
                  >
                    {mappingTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TestTube2 className="w-3.5 h-3.5" />}
                    Probar
                  </Button>
                </div>
              )}
              {mappingTestResult && (
                <div
                  data-testid={ATID.automationSourceMappingTestResult}
                  className="mt-2 text-xs bg-lightgray/40 border border-softblue p-2"
                >
                  {mappingTestResult.resolved_category ? (
                    <>Resuelto → <strong>{mappingTestResult.resolved_category.name}</strong> (<code>{mappingTestResult.resolved_category.slug}</code>)</>
                  ) : (
                    <em className="text-amber-700">Ninguna regla activa matcheó el texto ingresado.</em>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray">Notas internas</Label>
              <Textarea
                data-testid={ATID.automationSourceFieldNotes}
                rows={2}
                value={form.notes || ""}
                onChange={(e) => setField({ notes: e.target.value })}
                placeholder="Observaciones internas sobre la fuente"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid={ATID.automationSourceCancel}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid={ATID.automationSourceSave}>
              {saving ? "Guardando…" : (editingId ? "Guardar cambios" : "Crear fuente")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent data-testid={ATID.automationTestDialog} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {testResult ? `Prueba: ${testResult.source?.name}` : "Probando fuente…"}
            </DialogTitle>
          </DialogHeader>
          {!testResult ? (
            <div className="py-8 text-center text-textgray">
              <Loader2 className="inline w-5 h-5 animate-spin mr-2" /> Obteniendo listado y primer artículo…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={testResult.reachable
                  ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                  : "border-red-300 text-red-700 bg-red-50"}>
                  {testResult.reachable ? "Accesible" : "No accesible"}
                </Badge>
                <Badge variant="outline" className="border-nightblue/40 text-nightblue">
                  {testResult.detected} noticia(s) detectada(s)
                </Badge>
                <Badge variant="outline">tipo: {testResult.source_type_used}</Badge>
              </div>

              {testResult.listing_url && (
                <div className="text-xs text-textgray break-all">
                  <span className="uppercase tracking-widest text-[10px] mr-1">Listado:</span>
                  <a href={testResult.listing_url} target="_blank" rel="noreferrer" className="text-turquoise hover:underline">
                    {testResult.listing_url}
                  </a>
                </div>
              )}

              {testResult.preview ? (
                <div className="bg-lightgray/50 border border-softblue p-4 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold">Vista previa del primer artículo</div>
                  <h4 className="font-serif font-bold text-blueblack">{testResult.preview.title || "(sin título)"}</h4>
                  {testResult.preview.summary && <p className="text-sm text-textgray italic">{testResult.preview.summary}</p>}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div><span className="uppercase text-[10px] tracking-widest text-textgray">Fecha: </span>{testResult.preview.published_at || "—"}</div>
                    <div><span className="uppercase text-[10px] tracking-widest text-textgray">Autor: </span>{testResult.preview.author || "—"}</div>
                  </div>
                  {testResult.preview.image_url && (
                    <div className="pt-1">
                      <div className="uppercase text-[10px] tracking-widest text-textgray mb-1">Imagen:</div>
                      <img src={testResult.preview.image_url} alt="preview" className="max-h-40 border border-softblue" />
                    </div>
                  )}
                  {testResult.preview.content_excerpt && (
                    <div className="pt-1">
                      <div className="uppercase text-[10px] tracking-widest text-textgray mb-1">Cuerpo (primer fragmento):</div>
                      <div className="text-xs bg-white p-2 border border-softblue max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: testResult.preview.content_excerpt + "…" }} />
                    </div>
                  )}
                  {testResult.preview.link && (
                    <a href={testResult.preview.link} target="_blank" rel="noreferrer" className="text-xs text-turquoise hover:underline flex items-center gap-1 pt-1">
                      <ExternalLink className="w-3 h-3" /> Abrir noticia original
                    </a>
                  )}
                </div>
              ) : (
                <div className="text-sm text-textgray italic">No se pudo extraer una vista previa.</div>
              )}

              {testResult.errors && testResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-800">
                  <div className="font-semibold mb-1">Errores</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {testResult.errors.slice(0, 6).map((e, i) => <li key={`err-${i}-${String(e).slice(0, 30)}`}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialogOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
